import axios from 'axios';
import { API_SEARCH_BFF, QueryType } from '@constants';
import {
  ProductsSearchParams,
  GetRelatedProductsParams,
  ConversationalProductsSearchResponse,
  SuggestedAnswer,
  ImageToQueryRequest,
  ImageToQueryResponse
} from './interfaces';
import { Product, ProductsResponse } from '@services/product';
import { ProductsRecommendationsResponse } from '@services/product/interfaces';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import { getUserPseudoId } from '@utils/userPseudoId';
import { API_HOST_PROXY } from '@constants/api';

export class SearchEngineBFFClient {
  private engine: string | undefined;
  constructor(engine?: string) {
    this.engine = engine ?? null;
  }

  public async getRelatedItems(params: GetRelatedProductsParams): Promise<ProductsResponse | null> {
    const res = await axios.post(`${API_SEARCH_BFF}/getRelatedItems`, {
      params: {
        ...params,
        ...(typeof window !== 'undefined' && { userPseudoId: getUserPseudoId() })
      },
      service: this.engine
    },
    {
      headers: isFeatureEnabled(FEATURES.MULTI_TENANCY) && params?.selectedTenantKey
        ? { 'volcano-tenant-key': params?.selectedTenantKey }
        : {}
    });

    return res.data;
  }

  public async getProductRecommendations(params: { categoryTree: string}): Promise<ProductsRecommendationsResponse | null> {
    const res = await axios.post(`${API_SEARCH_BFF}/getProductRecommendations`, {
      params,
      service: this.engine
    });

    return res.data;
  }

  public async getProductRecommendationsAWS(params: { categoryId: string}): Promise<ProductsRecommendationsResponse | null> {
    const res = await axios.post(`${API_SEARCH_BFF}/getProductRecommendations`, {
      params,
      service: this.engine
    });

    return res.data;
  }

  public async getProductsSearch(params: ProductsSearchParams): Promise<ProductsResponse> {
    const res = await axios.post(`${API_SEARCH_BFF}/getProductsSearch`, {
      params: {
        ...params,
        ...(params.query && { searchKeyword: params.query }),
        ...(typeof window !== 'undefined' && { userPseudoId: getUserPseudoId() })
      },
      service: this.engine
    },
    {
      headers: ((): Record<string, string> => {
        const tenantKey = params?.selectedTenantKey || axios.defaults.headers.common['volcano-tenant-key'] as string | undefined;
        if (!isFeatureEnabled(FEATURES.MULTI_TENANCY) || !tenantKey) return {};

        return { 'volcano-tenant-key': tenantKey };
      })()
    });

    return res.data;
  }

  /**
   * Unified conversational product search method that handles both initial and follow-up searches
   * @param params - Search parameters including query, conversation context, and user responses
   * @returns Promise resolving to conversational search response
   */
  public async getConversationalProductsSearch(params: ProductsSearchParams & {
    answer?: SuggestedAnswer;
    textAnswer?: string;
  }): Promise<ConversationalProductsSearchResponse> {
    const isInitialSearch = !params.conversationId && (!params.contents || params.contents.length === 0);

    if (isInitialSearch) {
      return this.performInitialSearch(params);
    } else {
      return this.performFollowUpSearch(params);
    }
  }

  /**
   * Performs initial conversational search with minimal request body
   */
  private async performInitialSearch(params: ProductsSearchParams): Promise<ConversationalProductsSearchResponse> {
    const requestBody = {
      query: params.query || '',
      contents: [],
      ...(params.conversationId && { conversationId: params.conversationId }),
      ...(params.sort && { sort: params.sort })
    };

    const res = await axios.post(`${API_HOST_PROXY}/search-assistant/messages`, requestBody);

    return res.data;
  }

  /**
   * Performs follow-up conversational search with full context and user response
   */
  private async performFollowUpSearch(params: ProductsSearchParams & {
    answer?: SuggestedAnswer;
    textAnswer?: string;
  }): Promise<ConversationalProductsSearchResponse> {
    const baseRequestBody = {
      query: params.query || '',
      refinedQueries: params.refinedQueries || [],
      contents: params.contents || [],
      queryTypes: [ QueryType.SIMPLE_PRODUCT_SEARCH ],
      conversationalFilteringSpec: {
        userAnswer: this.buildUserAnswer(params.answer, params.textAnswer)
      },
      filters: params.filters || [],
      pageInfo: {
        offset: params.offset || 0,
        limit: params.limit || 10
      },
      previousSuccessfulSearchRequest: params.previousSuccessfulSearchRequest,
      ...(params.conversationId && { conversationId: params.conversationId }),
      ...(params.sort && { sort: params.sort })
    };

    const res = await axios.post(`${API_HOST_PROXY}/search-assistant/messages`, baseRequestBody);

    return res.data;
  }

  /**
   * Converts an uploaded image to a search query using AI
   * @param request - Image file and optional user notes
   * @returns Promise resolving to image-to-query response
   */
  public async imageToQuery(request: ImageToQueryRequest): Promise<ImageToQueryResponse> {
    const formData = new FormData();
    formData.append('image', request.image);

    if (request.userNotes) {
      formData.append('userNotes', request.userNotes);
    }

    const res = await axios.post(`${API_HOST_PROXY}/search-assistant/image-to-query`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return res.data;
  }

  /**
   * Builds the userAnswer object based on the type of user response
   */
  private buildUserAnswer(
    answer?: SuggestedAnswer,
    textAnswer?: string
  ): { selectedAnswer?: { productAttributeValue: { name: string, value: string } }; textAnswer?: string } {
    const userAnswer: {
      selectedAnswer?: { productAttributeValue: { name: string, value: string } };
      textAnswer?: string
    } = {};

    if (answer) {
      userAnswer.selectedAnswer = {
        productAttributeValue: {
          name: answer.name,
          value: answer.value
        }
      };
    }

    if (textAnswer) {
      userAnswer.textAnswer = textAnswer;
    }

    return userAnswer;
  }

  public async getProductSuggestions(
    query: string
  ): Promise<Product[]> {
    const res = await axios.post(`${API_SEARCH_BFF}/getProductSuggestions`, {
      params: {
        searchKeyword: query,
        ...(typeof window !== 'undefined' && { userPseudoId: getUserPseudoId() })
      },
      service: this.engine
    });

    return res.data;
  }

  public async getQuerySuggestions(query: string): Promise<string[]> {
    const res = await axios.post(`${API_SEARCH_BFF}/getQuerySuggestions`, {
      params: {
        searchKeyword: query,
        ...(typeof window !== 'undefined' && { userPseudoId: getUserPseudoId() })
      },
      service: this.engine
    });

    return res.data;
  }

  public async sendConversionEvent(productIDs: string[], userToken?: string | null): Promise<void> {
    return await axios.post(`${API_SEARCH_BFF}/sendConversionEvent`, {
      params: {
        productIDs,
        userToken,
        ...(typeof window !== 'undefined' && { userPseudoId: getUserPseudoId() })
      },
      service: this.engine
    });
  }

  public async sendViewEvent(productIDs: string[], userToken?: string | null): Promise<void> {
    return await axios.post(`${API_SEARCH_BFF}/sendViewEvent`, {
      params: {
        productIDs,
        userToken,
        ...(typeof window !== 'undefined' && { userPseudoId: getUserPseudoId() })
      },
      service: this.engine
    });
  }

  public async sendClickEvent(productIDs: string[], positions: number[], userToken?: string | null): Promise<void> {
    return await axios.post(`${API_SEARCH_BFF}/sendClickEvent`, {
      params: {
        productIDs,
        userToken,
        positions,
        ...(typeof window !== 'undefined' && { userPseudoId: getUserPseudoId() })
      },
      service: this.engine
    });
  }

  public async getSuggestions(
    query: string
  ): Promise<{
  querySuggestions: string[];
  productSuggestions: Product[];
  userQuestions: { question: string }[];
}> {
    const res = await axios.post(`${API_SEARCH_BFF}/suggestions`, {
      params: {
        searchKeyword: query,
        ...(typeof window !== 'undefined' && { userPseudoId: getUserPseudoId() })
      },
      service: this.engine
    });

    const data = res?.data ?? {};

    return { querySuggestions: data.querySuggestions ?? [],
      productSuggestions: data.productSuggestions ?? [],
      userQuestions: data.userQuestions ?? []
    };
  }

  public async getConversationStarterExamples(): Promise<string[]> {
    const res = await axios.get(`${API_SEARCH_BFF}/getConversationStarterExamples`, {
      headers: { 'X-HF-Instance': 'true' }
    });

    return res?.data ?? [];
  }
}
